import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material';

const DEFAULT_MINUTES = 180;
const QUICK_PRESETS = [30, 60, 180];

const getCsrfToken = () => {
    const tokenTag = document.querySelector('meta[name="csrf-token"]');
    return tokenTag ? tokenTag.getAttribute('content') ?? '' : '';
};

const formatLocalDateTime = (value) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString();
};

const FluxPauseModal = ({ pauseUrlTemplate, resumeUrlTemplate }) => {
    const csrfToken = useMemo(() => getCsrfToken(), []);
    const [state, setState] = useState({
        open: false,
        itemId: null,
        itemName: '',
        minutes: DEFAULT_MINUTES,
        pausedUntil: null,
        isPaused: false,
        isSubmitting: false,
        error: null,
        success: null,
    });

    useEffect(() => {
        const handlePauseEvent = (event) => {
            const detail = event.detail || {};
            const itemId = detail.itemId ? String(detail.itemId) : null;
            if (!itemId) {
                return;
            }
            setState({
                open: true,
                itemId,
                itemName: detail.itemName || '',
                minutes: DEFAULT_MINUTES,
                pausedUntil: detail.pausedUntil || null,
                isPaused: Boolean(detail.isPaused),
                isSubmitting: false,
                error: null,
                success: null,
            });
        };

        window.addEventListener('items:pause', handlePauseEvent);
        return () => window.removeEventListener('items:pause', handlePauseEvent);
    }, []);

    const handleClose = useCallback(() => {
        if (state.isSubmitting) {
            return;
        }
        setState((prev) => ({ ...prev, open: false, error: null, success: null }));
    }, [state.isSubmitting]);

    const handleMinutesChange = useCallback((event) => {
        const rawValue = event.target.value;
        const parsed = parseInt(rawValue, 10);
        const value = Number.isNaN(parsed) ? '' : Math.max(parsed, 1);
        setState((prev) => ({ ...prev, minutes: value }));
    }, []);

    const setPresetMinutes = useCallback((minutes) => {
        setState((prev) => ({ ...prev, minutes }));
    }, []);

    const pauseUrl = useMemo(() => {
        if (!state.itemId || !pauseUrlTemplate) {
            return '';
        }
        return pauseUrlTemplate.replace('item:', state.itemId);
    }, [pauseUrlTemplate, state.itemId]);

    const resumeUrl = useMemo(() => {
        if (!state.itemId || !resumeUrlTemplate) {
            return '';
        }
        return resumeUrlTemplate.replace('item:', state.itemId);
    }, [resumeUrlTemplate, state.itemId]);

    const refreshTable = () => {
        if (window.table && typeof window.table.ajax?.reload === 'function') {
            window.table.ajax.reload(null, false);
        }
    };

    const handlePauseSubmit = useCallback(async () => {
        if (!pauseUrl) {
            setState((prev) => ({ ...prev, error: 'Pause endpoint is missing.' }));
            return;
        }

        const minutes = parseInt(state.minutes, 10);
        if (!minutes || minutes < 1) {
            setState((prev) => ({ ...prev, error: 'Please enter a valid number of minutes.' }));
            return;
        }

        setState((prev) => ({ ...prev, isSubmitting: true, error: null, success: null }));

        const formData = new FormData();
        formData.append('_token', csrfToken);
        formData.append('minutes', String(minutes));

        try {
            const response = await fetch(pauseUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                },
                body: formData,
            });

            const payload = await response.json();
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Unable to pause the flux.');
            }

            setState((prev) => ({
                ...prev,
                isSubmitting: false,
                success: 'Pause scheduled successfully.',
                pausedUntil: payload.paused_until || prev.pausedUntil,
                isPaused: true,
            }));
            refreshTable();
        } catch (error) {
            setState((prev) => ({
                ...prev,
                isSubmitting: false,
                error: error.message || 'Unable to pause the flux.',
            }));
        }
    }, [csrfToken, pauseUrl, state.minutes]);

    const handleResumeSubmit = useCallback(async () => {
        if (!resumeUrl) {
            setState((prev) => ({ ...prev, error: 'Resume endpoint is missing.' }));
            return;
        }

        setState((prev) => ({ ...prev, isSubmitting: true, error: null, success: null }));

        const formData = new FormData();
        formData.append('_token', csrfToken);

        try {
            const response = await fetch(resumeUrl, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                },
                body: formData,
            });

            const payload = await response.json();
            if (!response.ok || !payload?.success) {
                throw new Error(payload?.message || 'Unable to resume the flux.');
            }

            setState((prev) => ({
                ...prev,
                isSubmitting: false,
                success: 'Flux resumed.',
                pausedUntil: null,
                isPaused: false,
            }));
            refreshTable();
        } catch (error) {
            setState((prev) => ({
                ...prev,
                isSubmitting: false,
                error: error.message || 'Unable to resume the flux.',
            }));
        }
    }, [csrfToken, resumeUrl]);

    const dialogTitle = state.isPaused ? 'Extend pause' : 'Pause flux';
    const pausedLabel = state.pausedUntil ? formatLocalDateTime(state.pausedUntil) : '';

    return (
        <Dialog open={state.open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent dividers>
                {state.itemName && (
                    <Box mb={2}>
                        <Typography variant="subtitle2" color="text.secondary">
                            Flux
                        </Typography>
                        <Typography variant="h6">{state.itemName}</Typography>
                    </Box>
                )}

                {state.isPaused && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Paused until {pausedLabel || 'scheduled resume'}.
                    </Alert>
                )}

                <Stack direction="row" spacing={1} flexWrap="wrap" mb={2}>
                    {QUICK_PRESETS.map((preset) => (
                        <Button
                            key={preset}
                            size="small"
                            variant="outlined"
                            onClick={() => setPresetMinutes(preset)}
                            disabled={state.isSubmitting}
                        >
                            +{preset} min
                        </Button>
                    ))}
                </Stack>

                <TextField
                    fullWidth
                    label="Minutes"
                    type="number"
                    inputProps={{ min: 1, max: 10080 }}
                    value={state.minutes}
                    onChange={handleMinutesChange}
                    disabled={state.isSubmitting}
                    helperText="Choose how long the flux should stay paused."
                />

                {state.error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {state.error}
                    </Alert>
                )}
                {state.success && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                        {state.success}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                {state.isPaused && (
                    <Button
                        onClick={handleResumeSubmit}
                        color="success"
                        disabled={state.isSubmitting}
                    >
                        Resume now
                    </Button>
                )}
                <Button onClick={handleClose} disabled={state.isSubmitting}>
                    Close
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    onClick={handlePauseSubmit}
                    disabled={state.isSubmitting}
                >
                    {state.isPaused ? 'Add minutes' : 'Pause'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FluxPauseModal;
